/**
 * @fileoverview App-level Redux actions for managing core application state.
 * 
 * This module defines action types and action creators for:
 * - Application initialization
 * - CSA (Credit Support Annex) data fetching
 * - End-of-day (EOD) date management
 * - SIMM version information
 * 
 * @module app/actions
 */

import { subscribe as subscribeError } from '../webcommon/errors/sagas';

import { subscribe } from '../common/errors/sagas';
import { notificationError } from '../common/errors/notificationTypes';

/** @const {string} Action type prefix for app module */
const PRE = 'APP/';

/**
 * Action Types
 */

/** @const {string} Dispatched when app component is loaded */
export const LOADED = `${PRE}LOADED`;

/** @const {string} Dispatched when all CSA data is successfully fetched */
export const FETCH_CSA_DATA_SUCCEEDED = `${PRE}FETCH_CSAS_SUCCEEDED`;

/** @const {string} Dispatched when CSA data fetch fails */
export const FETCH_CSA_DATA_FAILED = `${PRE}FETCH_CSAS_FAILED`;

/** @const {string} Dispatched when monitoring CSA data is successfully fetched */
export const FETCH_MONITORING_CSA_DATA_SUCCEEDED = `${PRE}FETCH_MONITORING_CSAS_SUCCEEDED`;

/** @const {string} Dispatched when monitoring CSA data fetch fails */
export const FETCH_MONITORING_CSA_DATA_FAILED = `${PRE}FETCH_MONITORING_CSAS_FAILED`;

/** @const {string} Dispatched when IM CSA data is successfully fetched */
export const FETCH_IM_CSA_DATA_SUCCEEDED = `${PRE}FETCH_IM_CSAS_SUCCEEDED`;

/** @const {string} Dispatched when IM CSA data fetch fails */
export const FETCH_IM_CSA_DATA_FAILED = `${PRE}FETCH_IM_CSAS_FAILED`;

/** @const {string} Dispatched to set application initialization status */
export const SET_INITIALISED = `${PRE}SET_INITIALISED`;

/** @const {string} Dispatched when CSA data contains undefined values */
export const FETCH_CSA_UNDEFINED_ERROR = `${PRE}FETCH_CSA_UNDEFINED_ERROR`;

/** @const {string} Dispatched when EOD date is successfully fetched */
export const FETCH_EOD_SUCCEEDED = `${PRE}FETCH_EOD_SUCCEEDED`;

/** @const {string} Dispatched when EOD date fetch fails */
export const FETCH_EOD_ERROR = `${PRE}FETCH_EOD_ERROR`;

/** @const {string} Dispatched when SIMM version is successfully fetched */
export const FETCH_SIMM_VERSION_SUCCEEDED = `${PRE}FETCH_SIMM_VERSION_SUCCEEDED`;

/** @const {string} Dispatched when SIMM version fetch fails */
export const FETCH_SIMM_VERSION_ERROR = `${PRE}FETCH_SIMM_VERSION_ERROR`;

/** @const {string} Dispatched when CSA data list is empty */
export const FETCH_CSA_DATA_EMPTY = `${PRE}FETCH_CSA_DATA_EMPTY`;

/**
 * Error Notification Subscriptions
 * These actions are automatically subscribed to the error notification system.
 */
subscribe(FETCH_CSA_DATA_FAILED, 'Failed to fetch the list of CSAs', notificationError);
subscribe(FETCH_MONITORING_CSA_DATA_FAILED, 'Failed to fetch the list of Monitoring CSAs', notificationError);
subscribe(FETCH_IM_CSA_DATA_FAILED, 'Failed to fetch the list of IM CSAs', notificationError);
subscribe(FETCH_EOD_ERROR, 'Failed to fetch the last batch date', notificationError);
subscribe(FETCH_CSA_DATA_EMPTY, 'Failed to fetch the list of CSAs', notificationError);
subscribeError(FETCH_CSA_UNDEFINED_ERROR, { position: 'tc', autoDismiss: false });

/**
 * Action Creators
 */

/**
 * Signals that the app component has been loaded and initialization should begin.
 * 
 * @returns {Object} Action object
 * @returns {string} action.type - LOADED action type
 * 
 * @example
 * // In component
 * useEffect(() => {
 *   dispatch(loaded());
 * }, []);
 */
export const loaded = () => ({
    type: LOADED
});

/**
 * Dispatched when CSA data is successfully fetched.
 * 
 * @param {Array<Object>} csas - Array of CSA objects
 * @param {string} csas[].csa - CSA identifier
 * @param {string} csas[].counterparty - Counterparty name
 * @param {string} csas[].type - CSA type (IM or Monitoring)
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_CSA_DATA_SUCCEEDED action type
 * @returns {Array} action.csas - Array of CSA objects
 * 
 * @example
 * // In saga
 * const csaList = yield call(api.csaList);
 * yield put(fetchCsaDataSucceeded(csaList));
 */
export const fetchCsaDataSucceeded = csas => ({
    type: FETCH_CSA_DATA_SUCCEEDED,
    csas
});

/**
 * Dispatched when CSA data list is empty.
 * 
 * @param {string} error - Error message describing why data is empty
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_CSA_DATA_EMPTY action type
 * @returns {string} action.error - Error message
 */
export const fetchCsaDataEmpty = error => ({
    type: FETCH_CSA_DATA_EMPTY,
    error
});

/**
 * Dispatched when CSA data fetch fails.
 * 
 * @param {string} error - Error message from the failed fetch operation
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_CSA_DATA_FAILED action type
 * @returns {string} action.error - Error message
 * 
 * @example
 * // In saga
 * try {
 *   const csas = yield call(api.csaList);
 *   yield put(fetchCsaDataSucceeded(csas));
 * } catch (error) {
 *   yield put(fetchCsaDataFailed(error.message));
 * }
 */
export const fetchCsaDataFailed = error => ({
    type: FETCH_CSA_DATA_FAILED,
    error
});

/**
 * Dispatched when monitoring CSA data is successfully fetched.
 * 
 * @param {Array<Object>} csas - Array of monitoring CSA objects
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_MONITORING_CSA_DATA_SUCCEEDED action type
 * @returns {Array} action.csas - Array of monitoring CSA objects
 */
export const fetchMonitoringCsaDataSucceeded = csas => ({
    type: FETCH_MONITORING_CSA_DATA_SUCCEEDED,
    csas
});

/**
 * Dispatched when monitoring CSA data fetch fails.
 * 
 * @param {string} error - Error message from the failed fetch operation
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_MONITORING_CSA_DATA_FAILED action type
 * @returns {string} action.error - Error message
 */
export const fetchMonitoringCsaDataFailed = error => ({
    type: FETCH_MONITORING_CSA_DATA_FAILED,
    error
});

/**
 * Dispatched when IM (Initial Margin) CSA data is successfully fetched.
 * 
 * @param {Array<Object>} csas - Array of IM CSA objects
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_IM_CSA_DATA_SUCCEEDED action type
 * @returns {Array} action.csas - Array of IM CSA objects
 */
export const fetchImCsaDataSucceeded = csas => ({
    type: FETCH_IM_CSA_DATA_SUCCEEDED,
    csas
});

/**
 * Dispatched when IM CSA data fetch fails.
 * 
 * @param {string} error - Error message from the failed fetch operation
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_IM_CSA_DATA_FAILED action type
 * @returns {string} action.error - Error message
 */
export const fetchImCsaDataFailed = error => ({
    type: FETCH_IM_CSA_DATA_FAILED,
    error
});

/**
 * Sets the application initialization status.
 * 
 * @param {boolean} initialised - Whether the application has completed initialization
 * @returns {Object} Action object
 * @returns {string} action.type - SET_INITIALISED action type
 * @returns {boolean} action.initialised - Initialization status
 * 
 * @example
 * // In saga after all initial data is loaded
 * yield put(setInitialised(true));
 */
export const setInitialised = initialised => ({
    type: SET_INITIALISED,
    initialised
});

/**
 * Dispatched when CSA data contains undefined or invalid values.
 * Shows a non-dismissible error notification to the user.
 * 
 * @param {string} title - Error title
 * @param {string} message - Detailed error message listing CSAs with issues
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_CSA_UNDEFINED_ERROR action type
 * @returns {string} action.title - Error title
 * @returns {string} action.message - Error message
 */
export const fetchCsasUndefinedError = (title, message) => ({
    type: FETCH_CSA_UNDEFINED_ERROR,
    title,
    message
});

/**
 * Dispatched when end-of-day (EOD) date is successfully fetched.
 * 
 * @param {string} data - EOD date in format YYYY-MM-DD
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_EOD_SUCCEEDED action type
 * @returns {string} action.data - EOD date
 * 
 * @example
 * // In saga
 * const eodDate = yield call(api.eodDate);
 * yield put(fetchEodSucceeded(eodDate)); // "2025-10-22"
 */
export const fetchEodSucceeded = data => ({
    type: FETCH_EOD_SUCCEEDED,
    data
});

/**
 * Dispatched when EOD date fetch fails.
 * 
 * @param {string} error - Error message from the failed fetch operation
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_EOD_ERROR action type
 * @returns {string} action.error - Error message
 */
export const fetchEodError = error => ({
    type: FETCH_EOD_ERROR,
    error
});

/**
 * Dispatched when SIMM version is successfully fetched.
 * 
 * @param {string} data - SIMM version number (e.g., "2.6")
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_SIMM_VERSION_SUCCEEDED action type
 * @returns {string} action.data - SIMM version
 * 
 * @example
 * // In saga
 * const simmVersion = yield call(api.simmVersion);
 * yield put(fetchSimmVersionSucceeded(simmVersion)); // "2.6"
 */
export const fetchSimmVersionSucceeded = data => ({
    type: FETCH_SIMM_VERSION_SUCCEEDED,
    data
});

/**
 * Dispatched when SIMM version fetch fails.
 * 
 * @param {string} error - Error message from the failed fetch operation
 * @returns {Object} Action object
 * @returns {string} action.type - FETCH_SIMM_VERSION_ERROR action type
 * @returns {string} action.error - Error message
 */
export const fetchSimmVersionError = error => ({
    type: FETCH_SIMM_VERSION_ERROR,
    error
});